import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { Row, Col, Card, Button, Form, Badge, Pagination, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { customReportAPI } from '../../utils/api';
import type { SavedReport } from '../../types/customReport';
import { 
  Add as AddIcon, 
  Search as SearchIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as PreviewIcon,
  GetApp as ExportIcon,
  People as SharedIcon,
  Description as ReportIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import DeleteConfirmModal from './DeleteConfirmModal';
import ExportModal from './ExportModal';
import { formatDateInNurseryTimezone } from '../../utils/timezone';

interface Props {
  onEdit: (reportId: number, mode: 'edit' | 'preview') => void;
  onCreateNew: () => void;
}

const SavedReportsList: React.FC<Props> = ({ onEdit, onCreateNew }) => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const { nursery_id } = useParams<{ nursery_id: string }>();
  const { user, currentNursery } = useAuth();
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(9);
  const [deletingReport, setDeletingReport] = useState<SavedReport | null>(null);
  const [exportingReport, setExportingReport] = useState<SavedReport | null>(null);

  const { canCreateCustomReports, canExportCustomReports, canManageCustomReports } = useRolePermissions();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customReportAPI.list(nursery_id!, {
        search: searchTerm,
        page,
        per_page: perPage
      });
      setReports(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch reports', error);
    } finally {
      setLoading(false);
    }
  }, [nursery_id, searchTerm, page, perPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReports();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, page, nursery_id, fetchReports]);

  const handleDelete = async () => {
    if (!deletingReport) return;
    try {
      await customReportAPI.delete(nursery_id!, deletingReport.id);
      fetchReports();
      setDeletingReport(null);
    } catch (error) {
      console.error('Failed to delete report', error);
    }
  };

  const canEditReport = (report: SavedReport) => {
    return canManageCustomReports || report.user_id === user?.id;
  };

  const getReportTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      attendance: 'primary',
      detections: 'info',
      violations: 'danger',
      timeline: 'secondary'
    };
    const bg = colors[type] ?? 'secondary';
    const label = t(`customReports.types.${type}`, {
      defaultValue: type
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    });
    return <Badge bg={bg}>{label}</Badge>;
  };

  const renderPagination = () => {
    const lastPage = Math.ceil(total / perPage);
    if (lastPage <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    const endPage = Math.min(lastPage, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(<Pagination.First key="first" onClick={() => setPage(1)} />);
      items.push(<Pagination.Prev key="prev" onClick={() => setPage(page - 1)} />);
      if (startPage > 2) items.push(<Pagination.Ellipsis key="e1" disabled />);
    }

    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item 
          key={p} 
          active={p === page}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (endPage < lastPage) {
      if (endPage < lastPage - 1) items.push(<Pagination.Ellipsis key="e2" disabled />);
      items.push(<Pagination.Next key="next" onClick={() => setPage(page + 1)} />);
      items.push(<Pagination.Last key="last" onClick={() => setPage(lastPage)} />);
    }

    return (
      <Pagination className="justify-content-center mt-4">
        {items}
      </Pagination>
    );
  };

  const renderSkeleton = () => {
    return (
      <Row>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Col key={i} md={4} className="mb-4">
            <Card className="h-100 shadow-sm border-0 skeleton-card" style={{ minHeight: '180px' }}>
              <Card.Body>
                <div className="skeleton-line mb-3" style={{ width: '40%', height: '14px' }}></div>
                <div className="skeleton-line mb-2" style={{ width: '80%', height: '20px' }}></div>
                <div className="skeleton-line mb-3" style={{ width: '100%', height: '12px' }}></div>
                <div className="d-flex gap-2 mt-auto pt-3">
                  <div className="skeleton-line" style={{ width: '25%', height: '30px' }}></div>
                  <div className="skeleton-line" style={{ width: '25%', height: '30px' }}></div>
                  <div className="skeleton-line" style={{ width: '25%', height: '30px' }}></div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div>
      {canCreateCustomReports && (
        <div className="d-flex justify-content-end mb-4">
          <Button variant="primary" onClick={onCreateNew}>
            <AddIcon className="me-1" /> {t('customReports.newReport')}
          </Button>
        </div>
      )}

      <div className="mb-4">
        <InputGroup className="shadow-sm">
          <InputGroup.Text className="bg-white border-end-0">
            <SearchIcon style={{ color: theme.palette.text.secondary }} />
          </InputGroup.Text>
          <Form.Control
            className="border-start-0 ps-0"
            placeholder={t('buttons.search', { ns: 'common' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading && reports.length === 0 ? renderSkeleton() : reports.length === 0 ? (
        <div className="text-center py-5">
          <ReportIcon style={{ fontSize: '64px', color: theme.palette.divider }} className="mb-3" />
          <div className="text-muted mb-3">{t('customReports.noReports')}</div>
          {canCreateCustomReports && (
            <Button variant="outline-primary" onClick={onCreateNew}>
              {t('customReports.noReportsDesc')}
            </Button>
          )}
        </div>
      ) : (
        <>
          <Row>
            {reports.map((report) => (
              <Col key={report.id} md={4} className="mb-4">
                <Card className="h-100 shadow-sm border-0 report-card hover-lift transition-all">
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0 text-truncate" title={report.name}>{report.name}</h6>
                      {getReportTypeBadge(report.report_type)}
                    </div>
                    <Card.Text className="text-muted small mb-3 flex-grow-1" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {report.description || <span className="fst-italic">{t('customReports.noDescription')}</span>}
                    </Card.Text>
                    <div className="d-flex align-items-center mb-3 small">
                      <span className="text-muted me-2">{report.creator?.name || t('customReports.system')}</span>
                      {report.is_shared && (
                        <Badge bg="light" text="dark" className="border">
                          <SharedIcon style={{ fontSize: '12px', marginInlineEnd: '4px' }} />
                          {t('customReports.shared')}
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-primary" size="sm" className="flex-grow-1 d-flex align-items-center justify-content-center" onClick={() => onEdit(report.id, 'preview')} title={t('buttons.view', { ns: 'common' })}>
                        <PreviewIcon style={{ fontSize: '16px' }} className="me-1" /> {t('buttons.view', { ns: 'common' })}
                      </Button>
                      {canEditReport(report) && (
                        <Button variant="outline-secondary" size="sm" onClick={() => onEdit(report.id, 'edit')} title={t('buttons.edit', { ns: 'common' })}>
                          <EditIcon style={{ fontSize: '16px' }} />
                        </Button>
                      )}
                      {canExportCustomReports && (
                        <Button variant="outline-info" size="sm" onClick={() => setExportingReport(report)} title={t('customReports.export')}>
                          <ExportIcon style={{ fontSize: '16px' }} />
                        </Button>
                      )}
                      {canEditReport(report) && (
                        <Button variant="outline-danger" size="sm" onClick={() => setDeletingReport(report)} title={t('buttons.delete', { ns: 'common' })}>
                          <DeleteIcon style={{ fontSize: '16px' }} />
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                  <Card.Footer className="bg-transparent border-top-0 pt-0 pb-3">
                    <small className="text-muted small">
                      {t('common:labels.createdAt', { defaultValue: 'Created' })}: {formatDateInNurseryTimezone(report.created_at, currentNursery?.timezone || 'UTC')}
                    </small>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>

          {renderPagination()}
        </>
      )}

      {deletingReport && (
        <DeleteConfirmModal 
          show={!!deletingReport} 
          onHide={() => setDeletingReport(null)} 
          onConfirm={handleDelete} 
          reportName={deletingReport.name}
        />
      )}

      {exportingReport && (
        <ExportModal 
          show={!!exportingReport} 
          onHide={() => setExportingReport(null)} 
          report={exportingReport} 
        />
      )}
    </div>
  );
};

export default SavedReportsList;
